import React from 'react';
import classnames from 'classnames';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED_BY_PARTNER,
} from 'common/householdConstants';

import {
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import {
  formatPrice,
  formatPhoneNumber,
} from 'common/formatters';

import {
  isApplicationPastStatus,
} from 'common/householdApplication/applicationHelpers';

import SrRenterApplicationStatusLinkButton from 'client/components/SrRenterApplicationStatusLinkButton';

const SrRenterApplicationStatusStepApproval = ({
  listing,
  lease,
  moveInDate,
  householdListing,
  householdApplication,
  earliestApplication,
  applicationRejected,
  householdApplicationsStatusUpdates,
  listingTenantScreening,
  listingFullService,
  listingSecurityDepositSettings,
  marketingPartnerListing,
  market,
  shouldCollectSecurityDepositAndFees,
  securityDepositPayingForLabel,
  SrCountdownClockStaticRefresh,
}) => {
  const securityDepositLabel = getMarketSecurityDepositLabel(market);

  if (
    (!isApplicationPastStatus(earliestApplication, HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD)
      || earliestApplication.status === HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD
    ) && !applicationRejected
  ) {
    const householdApplicationStatusPendingLanlord = householdApplicationsStatusUpdates
      .find(({ toStatus }) => toStatus === HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD);

    const startDate = (
      householdApplicationStatusPendingLanlord
      && momentWithAustinTimezone(householdApplicationStatusPendingLanlord.createdDate)
        .isSame(momentWithAustinTimezone(), 'day'))
      ? householdApplicationStatusPendingLanlord.createdDate
      : momentWithAustinTimezone().set('hour', 7).set('minute', 0).toISOString();

    if (listingTenantScreening) {
      return (
        <>
          <div className='SrRenterApplicationStatusDescription'>
            Sunroom has bundled your application and
            sent it over to the landlord for approval.
            Sometimes it can take over 24 hours, so
            please bare with us. We know this is an
            anxious part of the process, so we will follow
            up with the landlord over and over and let you
            know each time we follow up below.
          </div><br />
          <SrCountdownClockStaticRefresh
            text='Followed up with Landlord'
            className='optionToDisable'
            startDate={startDate}
          />
        </>
      );
    }

    return (
      <>
        <div className='SrRenterApplicationStatusDescription'>
          The landlord is reviewing your application. Sometimes it can take over
          24 hours to be approved. Sunroom will reach out to the landlord every
          morning at 7AM to remind them to view your application.
        </div><br />
        <SrCountdownClockStaticRefresh
          text='Last outreach to landlord'
          className='optionToDisable'
          startDate={startDate}
        />
        <div className='SrRenterApplicationStatusSubheader'>
          what’s next if you're approved?
        </div>
        <div className='SrRenterApplicationStatusDescription'>
          {shouldCollectSecurityDepositAndFees
            ? (
              'The next step is to make the required payments. Being approved does not ' +
              'guarantee the rental is yours. Making the required payments locks in your rental 🏠'
            )
            : (
              'You will soon receive instructions from Sunroom on how to pay the ' +
              `${securityDepositPayingForLabel}. Please pay the ${securityDepositLabel} within the next 24 ` +
              `hours. This property is reserved for you once you pay the ${securityDepositLabel}.`
            )
          }
        </div>
      </>
    );
  }

  if (
    isApplicationPastStatus(earliestApplication, HOUSEHOLD_APPLICATION_STATUS_ACCEPTED)
    && !applicationRejected
  ) {
    let descriptionDOM = null;
    if (listingTenantScreening) {
      descriptionDOM = (
        <>
          <span className='strong'>What’s next?</span>
          <br />
          <span>
            Your landlord will be in contact shortly to coordinate payments and sign the lease agreement.
            <br />
            {marketingPartnerListing?.user && (
              <>
                If you have any questions contact your landlord directly at:
                {marketingPartnerListing.user.email && (
                  <>
                    <br />
                    <span className='strong'>
                      {marketingPartnerListing.user.email}
                    </span>
                  </>
                )}
                <br />
                <span className='strong'>
                  {formatPhoneNumber(marketingPartnerListing.user.phone)}
                </span>
              </>
            )}
          </span>
        </>
      );
    }
    else if (shouldCollectSecurityDepositAndFees) {
      descriptionDOM = (
        `The next step is to pay the ${securityDepositPayingForLabel} ` +
        'within 24 hours after approval. Please make sure you have those funds set aside. ' +
        `This property is reserved for you once you pay the ${securityDepositLabel}. ` +
        'It\'s paid securely online, in the next step below this one.'
      );
    }
    else if (listingSecurityDepositSettings.securityDepositCollectionInstructions) {
      descriptionDOM = (
        'Follow the instructions below to pay the ' +
        `${securityDepositPayingForLabel} within 24 hours.`
      );
    }
    else {
      descriptionDOM = (
        'You will soon receive instructions from Sunroom on how to pay the ' +
        `${securityDepositPayingForLabel}. Please pay the ${securityDepositLabel} within the next 24 ` +
        `hours. This property is reserved for you once you pay the ${securityDepositLabel}.`
      );
    }

    return (
      <>
        <div className='approvedImageContainer'>
          <img className='approvedImage' alt='approved' src='/images/high-five-happy.svg' />
        </div>
        <h3 className='approvedHeader'>landlord approved!</h3>
        <div
          className={classnames('SrRenterApplicationStatusDescription', {
            centerText: listingFullService,
          })}
        >
          Congratulations on your new home!!
          Your move-in date is
          {` ${momentWithAustinTimezone(lease?.startDate || moveInDate).format('M/D/YYYY')} `}
          and the lease is for {lease?.term || householdApplication.leaseTerm} months
          with a {formatPrice(lease?.rentPrice || householdListing?.rentPrice || listing.price)} rent.
        </div>
        <div
          className={classnames('SrRenterApplicationStatusDescription', {
            centerText: listingFullService,
          })}
        >
          {descriptionDOM}
        </div>
      </>
    );
  }

  return (
    <>
      <div className='deniedMessageContainer'>
        <div className='deniedMessageHeader'>
          <span className='emoji' role='img' aria-label='sad face emoji'>&#128532;</span>
          Landlord denied your application
        </div>
        {householdListing.rejectedForCreditReport && (
          <div className='text'>
            This decision was based in whole or in part on the information provided to us in a Consumer Report
            by a consumer‐reporting agency. Their mailing address and phone number are listed below:
            <br /><br />
            Tenant Alert<br />
            23801 Calabasas Rd, Ste 1022<br />
            Calabasas, CA 91302<br />
            818-264-4537<br />
            www.tenantalert.com<br />
            <br />
            Tenant Alert plays no part in the decision to take any action on your rental application
            and is unable to provide you with specific reason(s) for our action.
            <br /><br />
            You have the right to a free copy of the report within 60 days of receipt of this notice
            and to dispute the completeness or accuracy of any information in the consumer report
            issued by the agency with Tenant Alert.
          </div>
        )}
        {householdListing.cancelReason === HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED_BY_PARTNER && (
          <div className='deniedMessage'>
            <span className='header'>Landlord’s message:</span>
            <br />
            {householdListing.rejectedReason}
          </div>
        )}
      </div>
      {listingFullService && (
        <>
          <div className='SrRenterApplicationStatusSubheader'>
            find homes to rent now
          </div>
          <div className='SrRenterApplicationStatusDescription'>
            Find homes you're qualified to rent. Tap the button below to search for more
            rentals in {listing.addressCity}
          </div>
          <SrRenterApplicationStatusLinkButton to='/feed' className='optionToDisable'>
            see rentals i'm qualified for &rarr;
          </SrRenterApplicationStatusLinkButton>
        </>
      )}
    </>
  );
};

SrRenterApplicationStatusStepApproval.displayName = 'SrRenterApplicationStatusStepApproval';

export default SrRenterApplicationStatusStepApproval;
